import { useMemo, useState } from 'react';

import {
  ColGroup,
  Column,
} from '../../../../../components/tables/basicTable/BasicTable.types';
import { TEXT_CONTENT } from '../../../../../utils/constants';
import {
  DATE_FORMAT,
  isValidDate,
  toDisplayDate,
} from '../../../../../utils/date';
import { toFixedDigit } from '../../../../../utils/number';
import PtArrivalsPtsCoveredModel, {
  PTS_COVERED_GRAPH_OPTIONS,
  TPtArrivalsPtsCovered,
} from './PtArrivalsPtsCovered.model';
import { ShiftAnnotaionInfo } from './PtArrivalsPtsCovered.types';

let columns: Column[] = [
  {
    field: 'category',
    headerName: '',
    sortable: false,
    sx: { minWidth: 150 },
    dataSx: {
      borderRight: '1px solid rgba(0,0,0,0.2)',
    },
  },
];

const columnGroupingModel: ColGroup[] = [
  {
    groupId: 'date',
    headerName: 'Day of Week',
    children: ['category'],
    width: '30ch',
  },
];

export default function useTableData(
  data: TPtArrivalsPtsCovered,
  day?: string,
  chartId?: string
) {
  const selectedDay = day ? day : '0';
  const [prevGraphAnnotaions, setPrevGraphAnnotaions] = useState<
    XAxisAnnotations[]
  >([]);
  const [graphOptions, setGraphOptions] = useState({
    ...PTS_COVERED_GRAPH_OPTIONS,
    chart: {
      ...(PTS_COVERED_GRAPH_OPTIONS?.chart || {}),
      id: chartId,
    },
  });

  const tableData = useMemo(() => {
    let newColGroupModel = [...columnGroupingModel];
    let newCols = [...columns];

    if (data) {
      const ScenarioObj = new PtArrivalsPtsCoveredModel(data);
      // .applyShiftChange(
      //   shiftInfo
      // );

      const { rows, cols } = ScenarioObj.getTableData(selectedDay);
      let { colGroups, cols: _cols } = getColumnAndGrpDef(cols || []);
      newColGroupModel = [...newColGroupModel, ...colGroups];
      newCols = [
        ...newCols,
        ..._cols,
        {
          field: 'total',
          headerName: TEXT_CONTENT.TOTAL,
          sortable: false,
          width: '17ch',
          renderCell: ({ row, allData }) => {
            if (row.id === 'patientsPerCorrectedHour') {
              const allArrivalValues =
                Object.values(allData?.find((d) => d.id === 'arrivals') || {})
                  ?.filter((d) => isValidDate(d))
                  ?.reduce((p, n) => p + n, 0) || 0;
              const allPhyValues =
                Object.values(
                  allData?.find((d) => d.id === 'physicianHourScheduled') || {}
                )
                  ?.filter((d) => isValidDate(d))
                  ?.reduce((p, n) => p + n, 0) || 0;
              const allAppValues =
                Object.values(
                  allData?.find((d) => d.id === 'appHourScheduled') || {}
                )
                  ?.filter((d) => isValidDate(d))
                  ?.reduce((p, n) => p + n, 0) || 0;
              const val = allArrivalValues / (allPhyValues + allAppValues / 2);
              return toFixedDigit(Number.isFinite(val) ? val : 0);
              // const allValues = Object.keys(row).filter((d) => isValidDate(d));
              // return toFixedDigit(
              //   allValues.reduce((acc, curr) => acc + (row[curr] || 0), 0) /
              //     allValues.length
              // );
            }
            return toFixedDigit(
              Object.keys(row)
                .filter((d) => isValidDate(d))
                .reduce((acc, curr) => acc + (row[curr] || 0), 0)
            );
          },
        },
      ];
      newColGroupModel.push({
        groupId: 'totalGrp',
        headerName: '',
        children: ['total'],
      });

      const { seriesData, shiftData = [] } =
        ScenarioObj.getGraphData(selectedDay);

      return {
        rows,
        columns: newCols,
        grpModel: newColGroupModel,
        seriesData,
        dayList: ScenarioObj.getAllDays(),
        shiftData,
      };
    }
    return {
      rows: [],
      columns: newCols,
      grpModel: newColGroupModel,
      seriesData: [],
      dayList: [],
      shiftData: [],
    };
  }, [data, selectedDay]);

  const shiftAnnotations = useMemo(() => {
    const shiftStartAnnotaionsInfo: ShiftAnnotaionInfo = {};
    const shiftEndAnnotaionsInfo: ShiftAnnotaionInfo = {};

    tableData.shiftData
      ?.filter(
        (d) =>
          selectedDay === '' ||
          new Date(d.shiftStart).getDay()?.toString() === selectedDay ||
          new Date(d.shiftEnd).getDay()?.toString() === selectedDay
      )
      ?.forEach?.((d, index) => {
        const startDt = new Date(d.shiftStart);
        const endDt = new Date(d.shiftEnd);
        const shiftType = d.providerType?.toLowerCase?.() || '';
        // let shiftAnnot = [];
        if (
          selectedDay === '' ||
          startDt.getDay()?.toString() === selectedDay
        ) {
          shiftStartAnnotaionsInfo[d.shiftStart] = [
            ...(shiftStartAnnotaionsInfo?.[d.shiftStart] || []),
            shiftType,
          ];
        }
        if (selectedDay === '' || endDt.getDay()?.toString() === selectedDay) {
          shiftEndAnnotaionsInfo[d.shiftEnd] = [
            ...(shiftEndAnnotaionsInfo?.[d.shiftEnd] || []),
            shiftType,
          ];
        }
      });

    return {
      start: shiftStartAnnotaionsInfo,
      end: shiftEndAnnotaionsInfo,
    };
  }, [selectedDay, tableData.shiftData]);

  const addShiftAnnotation = (
    xAxis: string | number,
    isShiftStart: boolean = true,
    shiftType?: string
  ) => {
    let label = isShiftStart ? TEXT_CONTENT.START : TEXT_CONTENT.END;
    return {
      id: 'shift-annotations',
      x: new Date(xAxis).valueOf(),
      borderColor: isShiftStart ? '#B1FECB' : '#FEB1B1',
      label: {
        text: `${shiftType === 'app' ? 'APP' : 'PHY'} - ${label}`,
        offsetY: shiftType === 'app' ? 18 : -35,
        offsetX: isShiftStart ? 0 : 18,
        // position: shiftType === 'physician' ? 'bottom' : 'top',
        style: {
          background: isShiftStart ? '#B1FECB' : '#FEB1B1',
          // fontWeight: 'bold',
          fontSize: '10px',
        },
      },
    };
  };
  const graphAnnotaionOptions = useMemo(() => {
    let graphOpt = [...(graphOptions.annotations?.xaxis || [])];
    graphOpt = graphOpt.filter((d) => d.id !== 'shift-annotations');
    Object.entries(shiftAnnotations?.start || []).forEach(([key, val]) => {
      val?.forEach((d) => graphOpt.push(addShiftAnnotation(key, true, d)));
    });
    Object.entries(shiftAnnotations?.end || []).forEach(([key, val]) => {
      val?.forEach((d) => graphOpt.push(addShiftAnnotation(key, false, d)));
    });
    return [...graphOpt];
  }, [
    graphOptions.annotations?.xaxis,
    shiftAnnotations?.end,
    shiftAnnotations?.start,
  ]);

  if (
    JSON.stringify(prevGraphAnnotaions) !==
    JSON.stringify(graphAnnotaionOptions)
  ) {
    setPrevGraphAnnotaions(graphAnnotaionOptions);
    setGraphOptions((prev) => ({
      ...prev,
      annotations: { ...prev.annotations, xaxis: graphAnnotaionOptions },
    }));
  }
  return { ...tableData, graphOptions, setGraphOptions };
}

export const getColumnAndGrpDef = (
  cols: Array<string | number> = [],
  isSummary: boolean = false
) => {
  let newCols: Column[] = [];
  let newColGroupModel: ColGroup[] = [];
  cols.forEach((d) => {
    let tempDate = new Date(d);
    newCols.push({
      field: `${tempDate.valueOf()}`,
      headerName: toDisplayDate(
        tempDate.toISOString(),
        DATE_FORMAT.TIME_HR_ONLY
      ),
      width: 25,
      sortable: false,
      dataSx: { position: 'relative' },
      renderCell: ({ value, row, colDef }) => {
        return toFixedDigit(value);
      },
    });
    /*******For Updating Group Name of table head **********/
    let grpHeadStartDate = new Date(tempDate.toISOString());
    grpHeadStartDate.setHours(0, 0, 0, 0);
    let grpHeadEndDate = new Date(grpHeadStartDate.toISOString());
    grpHeadEndDate.setHours(23);

    let findGrpModelRowIndex = newColGroupModel.findIndex((d) => {
      if (
        isValidDate(d.groupId) &&
        new Date(d.groupId)?.toDateString() === tempDate.toDateString()
      ) {
        return true;
      }
      return false;
    });
    if (findGrpModelRowIndex === -1) {
      newColGroupModel.push({
        groupId: grpHeadStartDate.toISOString(),
        headerName: toDisplayDate(
          grpHeadStartDate.toISOString(),
          DATE_FORMAT.DAY_ONLY
        ),
        children: [
          `${grpHeadStartDate.valueOf()}`,
          `${grpHeadEndDate.valueOf()}`,
        ],
      });
    } else {
      let _temp = newColGroupModel[findGrpModelRowIndex];
      let _tempChildren = _temp?.children || [];
      if (!_tempChildren.includes(`${tempDate.valueOf()}`)) {
        _tempChildren.push(`${tempDate.valueOf()}`);
        newColGroupModel[findGrpModelRowIndex] = {
          ..._temp,
          children: _tempChildren,
        };
      }
    }

    /*******************************************************/
  });
  return {
    cols: newCols || [],
    colGroups: newColGroupModel || [],
  };
};
